import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, Save, XCircle, AlertCircle, CheckCircle, Tag } from 'lucide-react';
import { supabase } from '../../utils/supabase';

const OffersCategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({
    category_name: '',
    description: '',
    emoji: '',
    is_active: true
  });
  const [message, setMessage] = useState(null);

  const resetForm = () => {
    setForm({
      category_name: '',
      description: '',
      emoji: '',
      is_active: true
    });
  };

  // Emojis disponibles para las categorías
  const availableEmojis = [
    { emoji: '🍽️', name: 'Plato de comida', category: 'Restaurantes' },
    { emoji: '🥐', name: 'Croissant', category: 'Desayunos' },
    { emoji: '😍', name: 'Cara con ojos de corazón', category: 'Recomendación' },
    { emoji: '🍕', name: 'Pizza', category: 'Comida rápida' },
    { emoji: '🍔', name: 'Hamburguesa', category: 'Comida rápida' },
    { emoji: '🍰', name: 'Pastel', category: 'Postres' },
    { emoji: '☕', name: 'Café', category: 'Bebidas' },
    { emoji: '🥗', name: 'Ensalada', category: 'Saludable' },
    { emoji: '🍜', name: 'Sopa', category: 'Comida caliente' },
    { emoji: '🍲', name: 'Olla de comida', category: 'Comida casera' },
    { emoji: '🥙', name: 'Pan relleno', category: 'Sándwiches' },
    { emoji: '🌮', name: 'Taco', category: 'Comida mexicana' },
    { emoji: '🍣', name: 'Sushi', category: 'Comida japonesa' },
    { emoji: '🍝', name: 'Espaguetis', category: 'Comida italiana' },
    { emoji: '🥘', name: 'Paella', category: 'Comida especial' },
    { emoji: '🍖', name: 'Carne', category: 'Carnes' },
    { emoji: '🐟', name: 'Pescado', category: 'Mariscos' },
    { emoji: '🥤', name: 'Bebida', category: 'Bebidas' },
    { emoji: '🍺', name: 'Cerveza', category: 'Bebidas alcohólicas' },
    { emoji: '🎂', name: 'Torta de cumpleaños', category: 'Celebraciones' },
    { emoji: '⭐', name: 'Estrella', category: 'Destacado' },
    { emoji: '🔥', name: 'Fuego', category: 'Ofertas calientes' },
    { emoji: '💎', name: 'Diamante', category: 'Premium' },
    { emoji: '🎯', name: 'Diana', category: 'Ofertas especiales' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      // Agregar timeout para evitar que se quede cargando indefinidamente
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout: La consulta tardó demasiado')), 10000)
      );

      const queryPromise = supabase
        .from('offers_categories')
        .select('*')
        .order('created_at', { ascending: false });

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error) {
        console.error('Error fetching categories:', error);
        setError(`Error al cargar las categorías: ${error.message}`);
        // Datos de ejemplo si hay error
        setCategories([
          { id: 1, category_name: 'Restaurantes', description: 'Ofertas de restaurantes y comida', emoji: '🍽️', is_active: true },
          { id: 2, category_name: 'Desayunos', description: 'Ofertas de desayunos y panadería', emoji: '🥐', is_active: true },
          { id: 3, category_name: 'Recomendación', description: 'Ofertas recomendadas especiales', emoji: '😍', is_active: true }
        ]);
      } else {
        setCategories(data || []);
        setError(null);
        console.log('Categorías cargadas exitosamente:', data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(`Error de conexión: ${error.message}`);
      // Datos de ejemplo si hay error
      setCategories([
        { id: 1, category_name: 'Restaurantes', description: 'Ofertas de restaurantes y comida', emoji: '🍽️', is_active: true },
        { id: 2, category_name: 'Desayunos', description: 'Ofertas de desayunos y panadería', emoji: '🥐', is_active: true },
        { id: 3, category_name: 'Recomendación', description: 'Ofertas recomendadas especiales', emoji: '😍', is_active: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.category_name.trim()) {
      setMessage({ type: 'error', text: 'El nombre de la categoría es obligatorio' });
      return;
    }

    try {
      const payload = {
        category_name: form.category_name.trim(),
        description: form.description.trim(),
        emoji: form.emoji,
        is_active: form.is_active
      };

      let result;
      if (editingCategory) {
        // Actualizar categoría existente
        result = await supabase
          .from('offers_categories')
          .update(payload)
          .eq('id', editingCategory.id)
          .select();
      } else {
        // Crear nueva categoría
        result = await supabase
          .from('offers_categories')
          .insert([payload])
          .select();
      }

      if (result.error) {
        console.error('Error saving category:', result.error);
        setMessage({ 
          type: 'error', 
          text: `Error al ${editingCategory ? 'actualizar' : 'crear'} la categoría: ${result.error.message}` 
        });
      } else {
        setMessage({ 
          type: 'success', 
          text: `Categoría ${editingCategory ? 'actualizada' : 'creada'} exitosamente` 
        });
        
        // Actualizar la lista local
        if (editingCategory) {
          setCategories(prev => prev.map(cat => 
            cat.id === editingCategory.id ? result.data[0] : cat
          ));
        } else {
          setCategories(prev => [result.data[0], ...prev]);
        }
        
        resetForm();
        setEditingCategory(null);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setMessage({ 
        type: 'error', 
        text: `Error al ${editingCategory ? 'actualizar' : 'crear'} la categoría` 
      });
    }

    // Limpiar mensaje después de 3 segundos
    setTimeout(() => setMessage(null), 3000);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setForm({
      category_name: category.category_name,
      description: category.description || '',
      emoji: category.emoji || '',
      is_active: category.is_active
    });
    setMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    resetForm();
    setMessage(null);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta categoría?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('offers_categories')
        .delete()
        .eq('id', categoryId);

      if (error) {
        console.error('Error deleting category:', error);
        setMessage({ type: 'error', text: 'Error al eliminar la categoría' });
      } else {
        setCategories(prev => prev.filter(cat => cat.id !== categoryId));
        setMessage({ type: 'success', text: 'Categoría eliminada exitosamente' });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setMessage({ type: 'error', text: 'Error al eliminar la categoría' });
    }

    // Limpiar mensaje después de 3 segundos
    setTimeout(() => setMessage(null), 3000);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        <p className="text-gray-600">Cargando categorías...</p>
        <button
          onClick={() => {
            setLoading(false);
            fetchCategories();
          }}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancelar y reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Tag className="w-8 h-8 mr-3 text-orange-500" />
          Gestión de Categorías de Ofertas
        </h2>
        <button
          onClick={fetchCategories}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Actualizar
        </button>
      </div>

      {/* Mensajes */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`p-4 rounded-lg flex items-center ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            {message.text}
            {message.type === 'error' && (
              <button
                onClick={fetchCategories}
                className="ml-4 px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Reintentar
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Formulario */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-6 rounded-xl shadow-lg border border-gray-200"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Plus className="w-5 h-5 mr-2 text-orange-500" />
          {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
        </h3>
        
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            name="category_name"
            placeholder="Nombre de la categoría"
            value={form.category_name}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />
          <textarea
            name="description"
            placeholder="Descripción de la categoría"
            value={form.description}
            onChange={handleInputChange}
            rows="2"
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />

          {/* Selector de Emoji */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Emoji de la Categoría
            </label>
            <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-2 p-4 border border-gray-300 rounded-lg bg-gray-50 max-h-40 overflow-y-auto">
              {availableEmojis.map((item, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, emoji: item.emoji }))}
                  className={`p-2 text-2xl rounded-lg border-2 transition-all duration-200 hover:scale-110 ${
                    form.emoji === item.emoji
                      ? 'border-orange-500 bg-orange-100 shadow-md'
                      : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50'
                  }`}
                  title={`${item.emoji} ${item.name} (${item.category})`}
                >
                  {item.emoji}
                </button>
              ))}
            </div>
            {form.emoji && (
              <div className="mt-2 flex items-center text-sm text-gray-600">
                <span className="text-2xl mr-2">{form.emoji}</span>
                <span>
                  {availableEmojis.find(item => item.emoji === form.emoji)?.name || 'Emoji seleccionado'}
                </span>
              </div>
            )}
          </div>

          {/* Checkbox de Activo */}
          <div className="md:col-span-2 flex items-center">
            <input
              type="checkbox"
              name="is_active"
              id="is_active_category"
              checked={form.is_active}
              onChange={handleInputChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active_category" className="ml-2 block text-sm text-gray-900">
              Activo
            </label>
          </div>

          <div className="md:col-span-2 flex justify-end space-x-4 mt-4">
            {editingCategory && (
              <motion.button
                type="button"
                onClick={handleCancelEdit}
                className="flex items-center px-6 py-3 bg-gray-300 text-gray-800 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <XCircle className="w-5 h-5 mr-2" /> Cancelar
              </motion.button>
            )}
            <motion.button
              type="submit"
              className="flex items-center px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Save className="w-5 h-5 mr-2" />
              {editingCategory ? 'Actualizar' : 'Crear'} Categoría
            </motion.button>
          </div>
        </form>
      </motion.div>

      {/* Lista de Categorías */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Categorías Existentes ({categories.length})
          </h3>
        </div>

        {error && (
          <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-yellow-400 mr-2" />
              <p className="text-yellow-700">{error}</p>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Emoji
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <motion.tr
                  key={category.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {category.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="text-2xl" title={category.emoji ? `Emoji: ${category.emoji}` : 'Sin emoji'}>
                      {category.emoji || '❓'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{category.category_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {category.description || 'Sin descripción'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      category.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {category.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <motion.button
                      onClick={() => handleEdit(category)}
                      className="text-orange-600 hover:text-orange-900 inline-flex items-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </motion.button>
                    <motion.button
                      onClick={() => handleDelete(category.id)}
                      className="text-red-600 hover:text-red-900 inline-flex items-center ml-4"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Eliminar
                    </motion.button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          {categories.length === 0 && !loading && (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No hay categorías registradas</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default OffersCategoriesManagement;
